import { Injectable } from '@angular/core';
import { PopoverImproved } from 'components/popover-host/popover-host';
import { MenuController } from './menu-controller';

interface BackContext_Base {}

interface BackContext_Popover {
  popover: PopoverImproved;
}
function isPopover(context: BackContext_Base): context is BackContext_Popover {
  return 'popover' in context;
}

interface BackContext_Menu {
  menu: MenuController;
}
function isMenu(context: BackContext_Base): context is BackContext_Menu {
  return 'menu' in context;
}

interface BackContext_Keyboard {
  keyboard: () => void; // Callback to stop keyboard
}
function isKeyboard(context: BackContext_Base): context is BackContext_Keyboard {
  return 'keyboard' in context;
}

@Injectable()
export class OverlayStackService {
  private contextStack: BackContext_Base[] = [];

  /**
   * Check if there's an active overlay (popover, menu, etc).
   * Called by BrowserHistory to determine if back should close overlay.
   * Validates entries against ground truth to avoid stale entries blocking navigation.
   */
  hasActiveOverlay(): boolean {
    // purge ALL stale entries from the stack (not just top — entries can be interleaved)
    this.contextStack = this.contextStack.filter(entry => {
      if (isPopover(entry) && entry.popover.dismissPromise) {
        return false; // already dismissed
      }
      // if menu entry but menu is no longer open (guard against uninitialized menuComponent)
      if (isMenu(entry) && entry.menu.menuComponent && !entry.menu.isOpen()) {
        return false; // menu closed
      }
      return true;
    });
    return this.contextStack.length > 0;
  }

  /**
   * Dismiss the top overlay.
   * Called by BrowserHistory when browser back should close overlay instead of navigating.
   */
  dismissTopOverlay(): void {
    if (this.contextStack.length > 0) {
      const context = this.contextStack.pop();
      if (isMenu(context)) {
        void context.menu.close();
      } else if (isPopover(context)) {
        context.popover.dismiss();
      } else if (isKeyboard(context)) {
        context.keyboard();
      }
    }
  }

  private pushContext<type extends BackContext_Base>(context: type) {
    this.contextStack.push(context);
  }

  private popContext<type extends BackContext_Base>(isType: (c: BackContext_Base) => c is type): type | undefined {
    if (this.contextStack.length === 0) return undefined;
    const top = this.contextStack[this.contextStack.length - 1];
    if (isType(top)) {
      this.contextStack.pop();
      return top;
    }
    return undefined;
  }

  public pushPopover(popover: PopoverImproved) {
    this.pushContext({ popover });
  }

  public popPopover() {
    this.popContext(isPopover);
  }

  public setMenu(menu: MenuController) {
    this.pushContext({ menu });
  }

  public unsetMenu() {
    this.popContext(isMenu);
  }

  public pushKeyboard(stopCallback: () => void) {
    this.pushContext({ keyboard: stopCallback });
  }

  public popKeyboard() {
    this.popContext(isKeyboard);
  }
}
